import { useState } from "react";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from "react-share";
import useFooddyStore from "../../store";
import { Card, Icon, OutlineButton, PrimaryButton, SectionTitle, rupees } from "./parts";
import { randomAvatar } from "./avatars";

const copyText = (text) => {
  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
  const textarea = document.createElement("textarea");
  textarea.value = text;
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  return Promise.resolve();
};

// "Share my spend": the user's spend card with share links to the public
// page (/share/:platform/:userId). Replaces the old full-page share layout.
function ShareView({ platform, platformName, onBack }) {
  const { userId, userName, gender, rank, totalAmount, userPrflPic, setUserPrflPic } = useFooddyStore();
  const [copied, setCopied] = useState(false);
  const amount = rupees(totalAmount);
  const shareUrl = `https://app.ecombullet.com/share/${platform}/${userId}`;
  const message = `Just tallied up my ${platformName} spending: ${amount}! 💸 I was shocked to see it add up like this. 😅 Have you checked yours? Let's compare!`;

  const copy = () =>
    copyText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });

  return (
    <>
      <SectionTitle title="Share my spend" sub={`Your ${platformName} card, ready to send to friends`}>
        <OutlineButton onClick={onBack}>
          <Icon name="arrow_back" size={16} />
          Back to overview
        </OutlineButton>
      </SectionTitle>

      <Card $pad="28px 24px" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 14 }}>
        <div style={{ position: "relative" }}>
          <img
            src={userPrflPic}
            alt=""
            style={{ width: 112, height: 112, borderRadius: "50%", background: "var(--acc-soft)", border: "4px solid var(--surface)", boxShadow: "0 0 0 1px var(--line)" }}
          />
          <button
            onClick={() => setUserPrflPic(randomAvatar(gender))}
            title="Change avatar"
            aria-label="Change avatar"
            style={{
              position: "absolute",
              right: 2,
              bottom: 2,
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid var(--line)",
              background: "var(--surface)",
              color: "var(--ink2)",
              display: "grid",
              placeItems: "center",
              padding: 0,
            }}
          >
            <Icon name="autorenew" size={18} />
          </button>
        </div>

        <div style={{ fontSize: 20, fontWeight: 600 }}>Hey buddy, I’m {userName || "here"}!</div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            padding: "10px 16px",
            borderRadius: 12,
            background: "var(--acc-soft)",
          }}
        >
          {rank && (
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600, color: "var(--acc)" }}>
              <Icon name="trophy" size={18} weight={400} />#{rank}
            </span>
          )}
          <span style={{ width: 1, height: 18, background: "var(--acc-mid)", opacity: 0.5 }} />
          <span style={{ fontSize: 22, fontWeight: 700, color: "var(--acc)" }}>{amount}</span>
        </div>

        <p style={{ margin: 0, maxWidth: 420, fontSize: 14, color: "var(--ink2)", lineHeight: 1.5 }}>
          Hey buddy, I spent {amount} on {platformName}. How about you? Let’s compare!
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center", marginTop: 6 }}>
          <PrimaryButton onClick={copy}>
            <Icon name={copied ? "check" : "link"} size={16} weight={400} />
            {copied ? "Link copied" : "Copy link"}
          </PrimaryButton>
          <WhatsappShareButton url={shareUrl} title={message}>
            <WhatsappIcon size={36} round />
          </WhatsappShareButton>
          <FacebookShareButton url={shareUrl} quote={message} hashtag="#Ecom">
            <FacebookIcon size={36} round />
          </FacebookShareButton>
          <TwitterShareButton url={shareUrl} title={message} hashtags={["ecom", "amazon", "flipkart", "spending_calculator"]}>
            <TwitterIcon size={36} round />
          </TwitterShareButton>
          <LinkedinShareButton url={shareUrl} title={`My ${platformName} spending`} summary={message}>
            <LinkedinIcon size={36} round />
          </LinkedinShareButton>
        </div>
      </Card>
    </>
  );
}

export default ShareView;
